<template>
  <div>
    <v-dialog v-model="dialogProgress" persistent max-width="500px">
      <v-card>
        <v-card-title>
          <span>{{ $t("import-export.title-progress") }}</span>
        </v-card-title>
        <v-card-text>
          {{ $t("import-export.progress") }}: {{ this.progressText }} <br />
          {{ $t("import-export.errors") }}: {{ this.progressErrors }}
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="dialogErrors" persistent max-width="500px">
      <v-card>
        <v-card-title>
          <span>{{ $t("import-export.title-found-errors") }}</span>
        </v-card-title>
        <v-card-text>
          <span v-if="importException">
            {{
              $t(
                `import-export.exceptions.${importException.code}`,
                importException.context
              )
            }}
          </span>
          <span v-if="hasImportErrors">
            {{
              $t("import-export.text-found-errors", {
                count: importErrors.length
              })
            }}
          </span>
          <v-btn
            v-if="hasImportErrors"
            v-on:click="this.downloadErrorFile"
            class="ml-2 flex-shrink-0"
            color="primary"
            >{{ $t("import-export.action-download-error-list") }}</v-btn
          >
          <v-btn
            v-on:click="
              dialogErrors = false;
              resetErrors();
            "
            class="ml-2 flex-shrink-0"
            color="secondary"
            >{{ $t("import-export.action-close") }}</v-btn
          >
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- ** ** ** IMPORT ** ** ** -->
    <v-row>
      <v-col cols="12">
        <h2>{{ $t("import-export.title-import") }}</h2>
        <p>
          {{ $t("addressbooks.text-import") }}
        </p>
        <ul>
          <li
            v-for="(translationKey, field) in fieldNames"
            :key="translationKey"
          >
            <b>{{ field }}</b> ({{ $t(translationKey) }})
          </li>
        </ul>

        <v-form ref="form" class="d-flex align-baseline">
          <v-file-input
            :label="this.$t('import-export.select-file-to-upload')"
            v-model="importFile"
            accept="text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            :rules="[rules.required]"
            show-size
            class="flex-grow-1"
          ></v-file-input>
          <v-btn
            :loading="dialogProgress"
            v-on:click="this.startImport"
            class="ml-2 flex-shrink-0"
            color="primary"
            upload
            >{{ $t("import-export.action-import") }}</v-btn
          >
        </v-form>
      </v-col>
    </v-row>

    <!-- ** ** ** EXPORT ** ** ** -->
    <v-row>
      <v-col cols="12">
        <h2>{{ $t("import-export.title-export") }}</h2>
        <p>
          {{ $t("addressbooks.text-export") }}
        </p>
        <v-btn
          :loading="dialogProgress"
          v-on:click="this.startExport"
          class="ml-2 flex-shrink-0"
          color="primary"
          upload
          >{{ $t("import-export.action-export") }}</v-btn
        >
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { rules } from "../mixins/rules";
import { DataImporter, ImportException } from "../../plugins/DataImporter";
import { DataExporter } from "../../plugins/DataExporter";

export default {
  name: "ImportExportAddressbook",
  mixins: [rules],
  props: ["id"],
  data: () => ({
    importFile: null,
    dialogProgress: false,
    dialogErrors: false,
    progressSuccesses: 0,
    progressErrors: 0,
    progressTotal: 0,
    importErrors: [],
    importException: undefined,
    exportLoading: false
  }),
  computed: {
    progressText: function() {
      if (this.progressTotal < 1) {
        return this.$t("import-export.counting");
      }

      let progressActual = this.progressSuccesses + this.progressErrors;

      return `${progressActual} / ${this.progressTotal}`;
    },
    fieldNames: function() {
      return {
        organization_name: "contacts.organization-name",
        given_name: "contacts.given-name",
        family_name: "contacts.family-name",
        additional_name: "contacts.additional-name",
        address_1: "contacts.address-1",
        address_2: "contacts.address-2",
        address_postcode: "contacts.address-postcode",
        address_city: "contacts.address-city",
        address_region: "contacts.address-region",
        address_country_code: "contacts.address-country",
        email: "contacts.email",
        telephone: "contacts.telephone",
        iban: "contacts.iban",
        notices: "contacts.notices",
        preferred_document_delivery: "contacts.preferred-document-delivery"
      };
    },
    importer: function() {
      return new DataImporter({
        allowedFields: Object.keys(this.fieldNames)
      });
    },
    hasImportErrors() {
      return this.importErrors.length > 0;
    }
  },
  methods: {
    async startImport() {
      if (!this.$refs.form.validate()) {
        return;
      }
      try {
        this.dialogProgress = true;
        this.importErrors = [];
        await this.importer.loadFile(this.importFile);
        let data = this.importer.validate().asJson();
        this.importList(data);
      } catch (e) {
        if (e instanceof ImportException) {
          this.importException = e;
          this.dialogProgress = false;
          this.dialogErrors = true;
        } else {
          throw e;
        }
      }
    },
    async importList(data) {
      this.progressTotal = data.length;
      const promises = [];
      for (const entity of data) {
        promises.push(this.importEntity(entity));
      }
      await Promise.allSettled(promises);
      this.dialogProgress = false;
      if (this.importErrors.length > 0) {
        this.dialogErrors = true;
      }
      this.resetProgress();
    },
    async importEntity(entity) {
      return this.$store.dispatch("contactStore/create", {
        item: { ...entity, _addressbook: { id: this.id } },
        onSuccess: () => {
          this.progressSuccesses++;
        },
        onError: res => {
          this.progressErrors++;
          this.importErrors.push({
            data: entity,
            errors: res.response?.data?.errors
          });
        },
        errorHandler: () => {},
        options: { notify: false }
      });
    },
    downloadErrorFile() {
      const output = this.importErrors.map(row => {
        const formattedErrors = [];
        for (const [field, errors] of Object.entries(row.errors)) {
          formattedErrors.push(`[${field}: ` + errors.join(" ") + `]`);
        }
        return {
          ...row.data,
          errors: formattedErrors.join(" ")
        };
      });
      new DataExporter().loadData(output).downloadCsv("import_errors");
    },
    resetProgress() {
      this.progressSuccesses = 0;
      this.progressErrors = 0;
      this.progressTotal = 0;
    },
    resetErrors() {
      this.importErrors = [];
      this.importException = undefined;
    },
    async startExport() {
      this.dialogProgress = true;
      let idsForExport = await this.exportFetchIds();
      this.exportList(idsForExport);
    },
    async exportFetchIds() {
      await this.$store.dispatch("contactStore/getList", {
        params: { limit: -1, filters: { addressbook: this.id } }
      });
      return this.$store.getters["contactStore/getList"].map(
        contact => contact.id
      );
    },
    async exportList(ids) {
      this.progressTotal = ids.length;
      const promises = [];
      for (const id of ids) {
        promises.push(this.exportEntity(id));
      }
      const contacts = (await Promise.allSettled(promises)).map(p => p.value);
      await new DataExporter().loadData(contacts).downloadCsv("export");
      this.dialogProgress = false;
      this.resetProgress();
    },
    async exportEntity(id) {
      const res = await this.$store.dispatch("contactStore/get", {
        id,
        onSuccess: () => {
          this.progressSuccesses++;
        },
        onError: () => {
          this.progressErrors++;
        }
      });
      const result = {};
      for (const field of Object.keys(this.fieldNames)) {
        result[field] = res.data[field];
      }
      return result;
    }
  }
};
</script>

<style scoped></style>
